import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { onSessionCheckin } from '../lib/points'
import { useAuth } from '../hooks/useAuth'
import { todayDay } from '../lib/helpers'

export default function CheckinPage() {
  const { club } = useAuth()
  const [todayClasses, setTodayClasses] = useState([])
  const [fighters, setFighters] = useState([])
  const [checkins, setCheckins] = useState([])
  const [activeClass, setActiveClass] = useState(null)
  const [mode, setMode] = useState('select') // 'select' | 'qr' | 'manual'
  const [loading, setLoading] = useState(true)
  const today = todayDay()

  const load = useCallback(async () => {
    if (!club) return
    const [cls, figs, cis] = await Promise.all([
      supabase.from('classes')
        .select('*, class_techniques(*, techniques(*))')
        .eq('club_id', club.id)
        .eq('day_of_week', today),
      supabase.from('fighters').select('*').eq('club_id', club.id).order('first_name'),
      supabase.from('checkins')
        .select('*, fighters(*), classes(*)')
        .gte('checked_in_at', new Date().toISOString().split('T')[0]),
    ])
    setTodayClasses(cls.data || [])
    setFighters(figs.data || [])
    setCheckins(cis.data || [])
    setLoading(false)
  }, [club, today])

  useEffect(() => { load() }, [load])

  // Poll for new check-ins every 5 seconds when QR is displayed
  useEffect(() => {
    if (mode !== 'qr') return
    const interval = setInterval(load, 5000)
    return () => clearInterval(interval)
  }, [mode, load])

  function startSession(cls) {
    setActiveClass(cls)
    setMode('qr')
  }

  if (loading) return <div style={{ padding: '2rem', color: '#888' }}>Loading...</div>

  if (mode === 'qr' && activeClass) {
    return (
      <TabletQRScreen
        cls={activeClass}
        club={club}
        checkins={checkins.filter(c => c.class_id === activeClass.id)}
        fighters={fighters}
        onBack={() => { setMode('select'); setActiveClass(null) }}
        onManual={() => setMode('manual')}
      />
    )
  }

  if (mode === 'manual' && activeClass) {
    return (
      <ManualCheckin
        cls={activeClass}
        fighters={fighters}
        checkins={checkins}
        onBack={() => setMode('qr')}
        onCheckin={load}
      />
    )
  }

  // Class selection screen
  return (
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: '1rem' }}>
        {today}'s classes
      </h3>

      {todayClasses.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#888' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <p style={{ fontSize: 14 }}>No classes scheduled for {today}.</p>
          <p style={{ fontSize: 12 }}>Go to Schedule to add one.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12, maxWidth: 600 }}>
          {todayClasses.map(cls => {
            const techs = cls.class_techniques?.map(ct => ct.techniques?.name).filter(Boolean) || []
            const checkedIn = checkins.filter(c => c.class_id === cls.id).length
            return (
              <div key={cls.id} style={{
                background: '#fff', border: '1px solid #eee', borderRadius: 12,
                padding: '1.25rem', display: 'flex', alignItems: 'center', gap: 16
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a1a' }}>{cls.name}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{cls.time_of_day} · {cls.class_type}</div>
                  {techs.length > 0 && (
                    <div style={{ fontSize: 12, color: '#1860a8', marginTop: 4 }}>{techs.join(', ')}</div>
                  )}
                  {checkedIn > 0 && (
                    <div style={{ fontSize: 12, color: '#1a7040', marginTop: 4 }}>
                      {checkedIn} checked in
                    </div>
                  )}
                </div>
                <button onClick={() => startSession(cls)} style={{
                  background: '#1860a8', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}>
                  Start check-in
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function TabletQRScreen({ cls, club, checkins, fighters, onBack, onManual }) {
  const checkinUrl = `${window.location.origin}/checkin/${cls.id}`
  const techs = cls.class_techniques?.map(ct => ct.techniques?.name).filter(Boolean) || []
  const checkedInIds = checkins.map(c => c.fighter_id)

  return (
    <div style={{
      minHeight: 'calc(100vh - 56px)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '2rem', background: '#f4f6fb'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>{cls.name}</div>
        <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>
          {cls.time_of_day} · {techs.length > 0 ? techs.join(', ') : 'Open mat'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '3rem', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* QR Code */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            background: '#fff', borderRadius: 20, padding: '2rem',
            border: '2px solid #eee', boxShadow: '0 4px 24px rgba(0,0,0,0.08)'
          }}>
            <QRCode value={checkinUrl} size={240} />
          </div>
          <p style={{ fontSize: 13, color: '#888', marginTop: 12 }}>
            Scan with your phone camera to check in
          </p>
          <p style={{ fontSize: 11, color: '#bbb', marginTop: 4, wordBreak: 'break-all', maxWidth: 280 }}>
            {checkinUrl}
          </p>
        </div>

        {/* Live roster */}
        <div style={{ minWidth: 240 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: '#1a1a1a' }}>
            Checked in ({checkins.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
            {checkins.length === 0 && (
              <div style={{ color: '#aaa', fontSize: 13 }}>Waiting for first check-in...</div>
            )}
            {checkins.map(ci => (
              <div key={ci.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: '#fff', borderRadius: 10, padding: '8px 12px',
                border: '1px solid #eee', animation: 'fadeIn 0.3s ease'
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', background: '#eaf3ff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, color: '#1860a8', flexShrink: 0
                }}>
                  {ci.fighters?.first_name?.[0]}{ci.fighters?.last_name?.[0]}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{ci.fighters?.first_name} {ci.fighters?.last_name}</div>
                  <div style={{ fontSize: 11, color: '#1a7040' }}>✓ Checked in</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, marginTop: '2.5rem' }}>
        <button onClick={onManual} style={{
          background: '#fff', color: '#444', border: '1px solid #ddd',
          borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer'
        }}>
          Manual check-in
        </button>
        <button onClick={onBack} style={{
          background: 'transparent', color: '#888', border: '1px solid #ddd',
          borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer'
        }}>
          Back
        </button>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }`}</style>
    </div>
  )
}

function ManualCheckin({ cls, fighters, checkins, onBack, onCheckin }) {
  const [selectedFighter, setSelectedFighter] = useState('')
  const [result, setResult] = useState(null)
  const [saving, setSaving] = useState(false)
  const checkedInIds = checkins.filter(c => c.class_id === cls.id).map(c => c.fighter_id)

  async function doCheckin() {
    if (!selectedFighter) return
    if (checkedInIds.includes(selectedFighter)) {
      setResult({ type: 'warn', msg: 'Already checked in!' }); return
    }

    // Check if membership is suspended
    const { data: sub } = await supabase
      .from('member_subscriptions')
      .select('status')
      .eq('fighter_id', selectedFighter)
      .single()

    if (sub?.status === 'suspended') {
      setResult({ type: 'warn', msg: 'Membership suspended — payment required before check-in.' }); return
    }

    setSaving(true)
    const fighter = fighters.find(f => f.id === selectedFighter)
    await supabase.from('checkins').insert({ fighter_id: selectedFighter, class_id: cls.id })

    // Increment both total sessions and belt sessions
    await supabase.from('fighters').update({
      sessions_count: (fighter.sessions_count || 0) + 1,
      belt_sessions: (fighter.belt_sessions || 0) + 1,
    }).eq('id', fighter.id)

    // Award session points + badges
    await onSessionCheckin(fighter, fighter.club_id)

    // Award technique points — each check-in = +1 star (0-10 scale)
    const techIds = cls.class_techniques?.map(ct => ct.technique_id) || []
    for (const techId of techIds) {
      const { data: ex } = await supabase.from('fighter_skills').select('*').eq('fighter_id', selectedFighter).eq('technique_id', techId).single()
      if (ex && ex.stars < 10) await supabase.from('fighter_skills').update({ stars: ex.stars + 1 }).eq('id', ex.id)
      else if (!ex) await supabase.from('fighter_skills').insert({ fighter_id: selectedFighter, technique_id: techId, stars: 1 })
    }

    const techNames = cls.class_techniques?.map(ct => ct.techniques?.name).filter(Boolean) || []
    setResult({
      type: 'ok',
      msg: `${fighter.first_name} checked in!`,
      detail: techNames.length ? `+1 star: ${techNames.join(', ')}` : 'Open mat'
    })
    setSaving(false)
    onCheckin()
  }

  return (
    <div style={{ maxWidth: 400 }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 13, marginBottom: '1rem', padding: 0 }}>
        ← Back to QR screen
      </button>
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', border: '1px solid #eee' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: 15, fontWeight: 600 }}>Manual check-in — {cls.name}</h3>
        <select value={selectedFighter} onChange={e => setSelectedFighter(e.target.value)}
          style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, marginBottom: 12 }}>
          <option value="">-- Select fighter --</option>
          {fighters.filter(f => !checkedInIds.includes(f.id)).map(f => (
            <option key={f.id} value={f.id}>{f.first_name} {f.last_name}</option>
          ))}
        </select>
        <button onClick={doCheckin} disabled={saving || !selectedFighter} style={{
          width: '100%', padding: 10, background: '#1860a8', color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer'
        }}>
          {saving ? 'Checking in...' : 'Check in'}
        </button>
        {result && (
          <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 13,
            background: result.type === 'ok' ? '#eaf7f0' : '#fff8e8',
            color: result.type === 'ok' ? '#1a7040' : '#a06000' }}>
            {result.msg}
          </div>
        )}
        <div style={{ marginTop: '1rem', borderTop: '1px solid #f0f0f0', paddingTop: '1rem' }}>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>Already checked in ({checkedInIds.length})</div>
          {fighters.filter(f => checkedInIds.includes(f.id)).map(f => (
            <div key={f.id} style={{ fontSize: 13, color: '#1a7040', padding: '3px 0' }}>✓ {f.first_name} {f.last_name}</div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Simple QR code generator using Google Charts API (no npm needed)
function QRCode({ value, size }) {
  const url = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=ffffff&color=1a1a1a&margin=1`
  return (
    <img src={url} alt="QR Code" width={size} height={size}
      style={{ display: 'block', borderRadius: 8 }} />
  )
}
